import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { DIALOG_DATA } from '@ansys-internal/awf-angular/shared';
import { State } from '@ansys-internal/awf-angular/store';
import {
  ButtonComponent,
  ButtonSize,
  ButtonType,
} from '@ansys-internal/awc-angular/buttons';
import { Icons, IconType } from '@ansys-internal/awc-angular/icons';
import { AWCListItem } from '@ansys-internal/awc-angular/lists';
import { ActiveTabState } from '@ansys-internal/awf-angular/workspace';
import { RequirementItemState } from 'src/app/state/lib/requirements.state';
import { AWCTreeItem } from '@ansys-internal/awc-angular/trees';
import { RequirementType } from 'src/app/shared/enums';
import { environment } from '../../../../environments/environment';
import { AuthEnvironment } from '@ansys-internal/awf-angular/auth';
import { DefaultProjectFileName } from '../../../actions/lib/common-strings.action';
import { ActionContributionsService } from '@ansys-internal/awf-angular/contributions';
import {
  InputComponent,
} from '@ansys-internal/awc-angular/forms';
import { FormSelectComponent } from '@ansys-internal/awc-angular/form-select';
import { CommonModule } from '@angular/common';
import { RequirementsService } from '../../../shared/services/requirements.service';
import { isVersionAtLeast } from '../../../shared/tools/common.utils';
import { SharedModule } from '../../../shared/shared.module';
import {SwitchComponent} from "@ansys-internal/awc-angular/switch";

@Component({
  selector: 'app-start-solve',
  templateUrl: './start-solve.component.html',
  styleUrls: ['./start-solve.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    InputComponent,
    FormSelectComponent,
    ButtonComponent,
    SwitchComponent,
    SharedModule,
  ],
})
export class StartSolveComponent implements OnInit {
  protected type: ButtonType = ButtonType.PRIMARY;
  protected suffixIcon: IconType = { icon: Icons.ONSCALE };
  protected staticRequirements: AWCListItem[] = [];
  protected dynamicRequirements: AWCListItem[] = [];
  protected dcRequirements: AWCListItem[] = [];
  protected cliVersions: AWCListItem[] = [];

  protected selectedStaticItems: string[] = [];
  protected selectedDynamicItems: string[] = [];
  protected selectedDCItems: string[] = [];
  protected selectedCliVersion: string = '';
  protected studyName: string = '';
  protected readonly environment = environment;
  protected readonly authEnvironment = AuthEnvironment;
  protected extraMemory: boolean = false;

  constructor(
    @Inject(DIALOG_DATA) public data: any,
    private state: State,
    private _cdr: ChangeDetectorRef,
    private actions: ActionContributionsService,
    private requirementsService: RequirementsService
  ) {
    const tab = this.state.value(ActiveTabState);
    const requirements = this.state.value(RequirementItemState, tab?.id) || [];
    requirements?.forEach((req: AWCTreeItem) => {
      const reqClone = { ...req };
      const reqType = req?.userData?.['requirement_type'];
      if (reqType === RequirementType.STATIC_ACCELERATION) {
        this.staticRequirements.push(reqClone);
        this.selectedStaticItems.push(reqClone.id);
      } else if (reqType === RequirementType.DYNAMIC_INPUT) {
        this.dynamicRequirements.push(reqClone);
        this.selectedDynamicItems.push(reqClone.id);
      } else if (reqType === RequirementType.DRIVE_CYCLE) {
        if (!reqClone.text) {
          reqClone.text = 'Drive Cycle';
        }
        this.dcRequirements.push(reqClone);
        this.selectedDCItems.push(reqClone.id);
      }
    });

    this.getCliVersions();
  }

  async ngOnInit(): Promise<void> {
    this.studyName = await this.actions.execute(DefaultProjectFileName);

    this._cdr.detectChanges();
  }

  protected async getCliVersions(): Promise<void> {
    const versions = await this.requirementsService.getVersions();
    const sortedVersions = versions
      .filter((v) => isVersionAtLeast(v.version, '1.0.50'))
      .sort((a, b) => (isVersionAtLeast(a.version, b.version) ? -1 : 1));

    this.cliVersions = [
      ...this.cliVersions,
      ...sortedVersions.map((version, index) => ({
        id: version.version,
        text: index === 0 ? `${version.version} (latest)` : version.version,
      })),
    ];

    this.selectedCliVersion = this.cliVersions[0].id;

    this._cdr.detectChanges();
  }

  protected confirm(): void {
    if (
      !this.studyName ||
      (!this.selectedStaticItems.length &&
        !this.selectedDynamicItems.length &&
        !this.selectedDCItems.length)
    ) {
      return;
    }

    this.data.confirm(
      this.studyName,
      [
        ...this.selectedStaticItems,
        ...this.selectedDynamicItems,
        ...this.selectedDCItems,
      ],
      this.extraMemory,
      this.selectedCliVersion
    );
  }

  protected setStudyName(name: string | number): void {
    this.studyName = name as string;
  }

  protected selectionStaticChanged(items: string[] | undefined): void {
    this.selectedStaticItems = items ?? [];
  }

  protected selectionDynamicChanged(items: string[] | undefined): void {
    this.selectedDynamicItems = items ?? [];
  }

  protected selectionDCChanged(items: string[] | undefined): void {
    this.selectedDCItems = items ?? [];
  }

  protected memoryBoost(memoryToggle: boolean): void {
    this.extraMemory = memoryToggle; //(event.target as HTMLInputElement).checked;
  }
}
